'use client';

import { useLocationTail } from '@/hooks/useLocationTail';
import {
  currentLocationTail,
  isTrackingSafeLocation,
} from '@/utils/trackingLocationGuard';
import { GoogleTagManager } from '@next/third-parties/google';
import { usePathname } from 'next/navigation';
import {
  type ReactElement,
  Suspense,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react';

/**
 * GTM is mounted only after the visitor has consented to analytics or
 * marketing (CookieConsent.tsx calls `window.updateGtmConsent`), and never on
 * a token-bearing route or while the location carries a sensitive param
 * (`isTrackingSafeLocation`, shared with the Meta pixel). Before consent it
 * must not load at all: even with every Consent Mode signal denied, a loaded
 * container still sends cookieless pings to google-analytics.com /
 * doubleclick.net, and that transmission from the visitor's device needs
 * consent under § 109 ods. 8 zákona č. 452/2021 Z. z. (EDPB Guidelines
 * 2/2023 on the technical scope of Art. 5(3)).
 */
export function shouldMountGtm(
  consented: boolean,
  pathname: string | null,
  locationTail: string = currentLocationTail()
): boolean {
  if (!consented) return false;
  return isTrackingSafeLocation(pathname, locationTail);
}

// The consent signal lives at MODULE scope, registered on `window` as soon
// as this module loads, so it is stored even when CookieConsent.tsx applies
// a saved choice before any GTMGuarded has mounted (or while the inner
// component is still suspended). Components read the stored value on mount
// and re-render on every change, so no ordering can drop a grant.
let gtmConsented = false;
const consentListeners = new Set<() => void>();

function setGtmConsent(enabled: boolean): void {
  gtmConsented = enabled;
  for (const listener of consentListeners) listener();
}

function subscribeToGtmConsent(listener: () => void): () => void {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
}

function registerGtmConsentCallback(): void {
  if (typeof window !== 'undefined') window.updateGtmConsent = setGtmConsent;
}

registerGtmConsentCallback();

/**
 * Test seam: forget the stored consent. Deliberately does NOT re-register
 * the window callback, so tests prove the module-load registration itself.
 */
export function resetGtmConsentForTests(): void {
  gtmConsented = false;
}

/**
 * `useLocationTail` reads `useSearchParams`, which needs a Suspense boundary
 * (this component sits in the root layout). The fallback is nothing, which is
 * also what GTM renders before consent, so the boundary costs no markup.
 */
export function GTMGuarded({ gtmId }: { gtmId: string }): ReactElement {
  return (
    <Suspense fallback={null}>
      <GTMGuardedInner gtmId={gtmId} />
    </Suspense>
  );
}

function GTMGuardedInner({ gtmId }: { gtmId: string }): ReactElement | null {
  const pathname = usePathname();
  // Query + fragment of THIS render (router query, live fragment), so the
  // guard never decides on a stale location after a soft navigation.
  const locationTail = useLocationTail();
  const consented = useSyncExternalStore(
    subscribeToGtmConsent,
    () => gtmConsented,
    () => false
  );
  // The LIVE location, checked after every commit (history is current by
  // then): on a soft navigation Next writes the new URL to history only after
  // render, so the render's tail can still be the previous page's. GTM
  // renders only once the live check passed too, i.e. never before it ran
  // (fail closed).
  const [liveLocationSafe, setLiveLocationSafe] = useState(false);
  useLayoutEffect(() => {
    setLiveLocationSafe(
      isTrackingSafeLocation(window.location.pathname, currentLocationTail())
    );
  });

  if (!liveLocationSafe) return null;
  if (!shouldMountGtm(consented, pathname, locationTail)) return null;
  return <GoogleTagManager gtmId={gtmId} />;
}
